/**
 * Gamepad Navigation for Menu Scenes
 *
 * Provides D-pad / analog-stick navigation between registered menu items,
 * A button to confirm, and B button to go back.
 * The focused item receives a pulsing gold highlight outline drawn via
 * an independent Graphics layer so existing stroke/hover logic is untouched.
 */

import Phaser from 'phaser'

/** A focusable menu item. */
interface MenuItem {
  /** The interactive rectangle that visually represents the item. */
  rect: Phaser.GameObjects.Rectangle
  /** Invoked when the player presses the confirm button (A / Cross). */
  onActivate: () => void
}

const DPAD = { UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const

export class MenuGamepadNavigator {
  private scene: Phaser.Scene
  private items: MenuItem[] = []
  private focusIndex: number = -1
  private backAction?: () => void

  // Graphics overlay for the highlight outline
  private highlight: Phaser.GameObjects.Graphics
  private pulseTween?: Phaser.Tweens.Tween

  // DAS-style repeat state for held directions
  private repeatStates = new Map<string, { held: boolean; elapsed: number; fired: boolean }>([
    ['prev', { held: false, elapsed: 0, fired: false }],
    ['next', { held: false, elapsed: 0, fired: false }],
  ])

  // Edge-detection for face buttons
  private prevA = false
  private prevB = false

  // Timing
  private readonly REPEAT_INITIAL = 300
  private readonly REPEAT_RATE = 180
  private readonly DEAD_ZONE = 0.3

  // Highlight style
  private readonly HIGHLIGHT_COLOR = 0xffdd00
  private readonly HIGHLIGHT_WIDTH = 4
  private readonly HIGHLIGHT_PADDING = 3
  private readonly HIGHLIGHT_RADIUS = 8
  private readonly HIGHLIGHT_DEPTH = 9998

  // Lifecycle bindings
  private updateBinding: () => void
  private shutdownBinding: () => void
  private destroyed = false

  constructor(scene: Phaser.Scene) {
    this.scene = scene

    this.highlight = scene.add.graphics()
    this.highlight.setDepth(this.HIGHLIGHT_DEPTH)
    this.highlight.setVisible(false)

    this.updateBinding = () => this.update(scene.game.loop.delta)
    this.shutdownBinding = () => this.destroy()

    scene.events.on('update', this.updateBinding)
    scene.events.once('shutdown', this.shutdownBinding)
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Register a focusable item. Returns `this` for chaining. */
  addItem(rect: Phaser.GameObjects.Rectangle, onActivate: () => void): this {
    this.items.push({ rect, onActivate })
    return this
  }

  /** Set the action triggered by the B / Circle button. */
  setBackAction(action: () => void): this {
    this.backAction = action
    return this
  }

  /** Remove all registered items and clear focus. */
  clearItems(): void {
    this.removeFocusHighlight()
    this.items = []
    this.focusIndex = -1
  }

  /** Tear down the navigator and free resources. Safe to call multiple times. */
  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true

    this.pulseTween?.stop()
    this.pulseTween = undefined

    if (this.highlight?.active) {
      this.highlight.destroy()
    }

    this.scene.events.off('update', this.updateBinding)
    this.scene.events.off('shutdown', this.shutdownBinding)
    this.items = []
    this.focusIndex = -1
  }

  // ---------------------------------------------------------------------------
  // Per-frame update
  // ---------------------------------------------------------------------------

  private update(dtMs: number): void {
    if (this.destroyed) return

    const pad = this.getConnectedPad()
    if (!pad) return

    this.processNavigation(pad, dtMs)
    this.processConfirmBack(pad)
  }

  // ---------------------------------------------------------------------------
  // D-pad / stick navigation with DAS repeat
  // ---------------------------------------------------------------------------

  private processNavigation(pad: Phaser.Input.Gamepad.Gamepad, dtMs: number): void {
    if (this.items.length === 0) return

    const up = pad.buttons[DPAD.UP]?.pressed ?? false
    const down = pad.buttons[DPAD.DOWN]?.pressed ?? false
    const left = pad.buttons[DPAD.LEFT]?.pressed ?? false
    const right = pad.buttons[DPAD.RIGHT]?.pressed ?? false

    const sx = pad.axes.length > 0 ? pad.axes[0].getValue() : 0
    const sy = pad.axes.length > 1 ? pad.axes[1].getValue() : 0

    const prevHeld = up || left || sy < -this.DEAD_ZONE || sx < -this.DEAD_ZONE
    const nextHeld = down || right || sy > this.DEAD_ZONE || sx > this.DEAD_ZONE

    this.handleRepeat('prev', prevHeld, -1, dtMs)
    this.handleRepeat('next', nextHeld, 1, dtMs)
  }

  private handleRepeat(key: string, held: boolean, dir: number, dtMs: number): void {
    const state = this.repeatStates.get(key)!

    if (held) {
      if (!state.held) {
        // Just pressed – fire immediately
        this.moveFocus(dir)
        state.held = true
        state.elapsed = 0
        state.fired = false
      } else {
        state.elapsed += dtMs
        const threshold = state.fired ? this.REPEAT_RATE : this.REPEAT_INITIAL
        if (state.elapsed >= threshold) {
          this.moveFocus(dir)
          state.elapsed -= threshold
          state.fired = true
        }
      }
    } else if (state.held) {
      state.held = false
      state.elapsed = 0
      state.fired = false
    }
  }

  private moveFocus(dir: number): void {
    if (this.items.length === 0) return

    let next: number
    if (this.focusIndex < 0) {
      // First activation – focus the first or last item depending on direction
      next = dir > 0 ? 0 : this.items.length - 1
    } else {
      next = this.focusIndex + dir
      if (next < 0) next = this.items.length - 1
      if (next >= this.items.length) next = 0
    }

    this.setFocus(next)
  }

  // ---------------------------------------------------------------------------
  // Confirm (A) / Back (B)
  // ---------------------------------------------------------------------------

  private processConfirmBack(pad: Phaser.Input.Gamepad.Gamepad): void {
    const a = pad.buttons[0]?.pressed ?? false
    const b = pad.buttons[1]?.pressed ?? false

    // A / Cross – confirm (edge-triggered)
    if (a && !this.prevA && this.focusIndex >= 0 && this.focusIndex < this.items.length) {
      this.items[this.focusIndex].onActivate()
    }

    // B / Circle – back (edge-triggered)
    if (b && !this.prevB) {
      this.backAction?.()
    }

    this.prevA = a
    this.prevB = b
  }

  // ---------------------------------------------------------------------------
  // Focus & highlight
  // ---------------------------------------------------------------------------

  private setFocus(index: number): void {
    this.focusIndex = index
    this.drawHighlight()
  }

  private drawHighlight(): void {
    if (this.destroyed) return

    this.highlight.clear()

    if (this.focusIndex < 0 || this.focusIndex >= this.items.length) {
      this.highlight.setVisible(false)
      this.pulseTween?.stop()
      return
    }

    const rect = this.items[this.focusIndex].rect
    const bounds = this.getWorldBounds(rect)
    const pad = this.HIGHLIGHT_PADDING

    this.highlight.lineStyle(this.HIGHLIGHT_WIDTH, this.HIGHLIGHT_COLOR, 1)
    this.highlight.strokeRoundedRect(
      bounds.x - pad,
      bounds.y - pad,
      bounds.width + pad * 2,
      bounds.height + pad * 2,
      this.HIGHLIGHT_RADIUS,
    )
    this.highlight.setVisible(true)

    // Restart pulsing animation
    this.pulseTween?.stop()
    this.highlight.setAlpha(1)
    this.pulseTween = this.scene.tweens.add({
      targets: this.highlight,
      alpha: { from: 1, to: 0.4 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })
  }

  private removeFocusHighlight(): void {
    if (this.destroyed) return
    this.highlight.clear()
    this.highlight.setVisible(false)
    this.pulseTween?.stop()
    this.pulseTween = undefined
  }

  // ---------------------------------------------------------------------------
  // Geometry helpers
  // ---------------------------------------------------------------------------

  /** Compute the world-space bounding box of a Rectangle, accounting for parent containers. */
  private getWorldBounds(rect: Phaser.GameObjects.Rectangle): {
    x: number
    y: number
    width: number
    height: number
  } {
    let worldX = rect.x
    let worldY = rect.y
    let parent: Phaser.GameObjects.Container | null = rect.parentContainer

    while (parent) {
      worldX += parent.x
      worldY += parent.y
      parent = parent.parentContainer
    }

    const w = rect.width * rect.scaleX
    const h = rect.height * rect.scaleY

    return {
      x: worldX - w * rect.originX,
      y: worldY - h * rect.originY,
      width: w,
      height: h,
    }
  }

  // ---------------------------------------------------------------------------
  // Gamepad helper
  // ---------------------------------------------------------------------------

  private getConnectedPad(): Phaser.Input.Gamepad.Gamepad | null {
    if (!this.scene?.input?.gamepad) return null
    const pads = this.scene.input.gamepad.gamepads
    if (!pads || pads.length === 0) return null

    for (const pad of pads) {
      if (pad && pad.connected) return pad
    }
    return null
  }
}
